import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import TriggerActionService from "../../components/TriggerActionService";
import ActionTriggerConstants from "../../components/ActionTriggerConstants";
import UnityBridge from "./utils/UnityBridge";
import ModalManager from "./utils/ModalManager";
import { getBuildName, getBuildVersion, setupTokens } from "./utils/UrlParams";
import { allowedPaymentOrigin, mustRedirectPayments, redirect } from "./utils/PaymentsUtils";
import Localize from "../../components/Localize/Localize";
import InstallBanner from "../../components/pwa/InstallBanner";
import "./Game.css";
import cookieManager from "../../components/CookieManager";
import Constants from "../../components/Constants";
import GeoLocationHelper from "../../components/Utils/GeoLocationHelper";

const LANDSCAPE_RATIO = 1920 / 1080;

const fitContain = (vw, vh, targetRatio) => {
    const viewportRatio = vw / vh;
    if (viewportRatio > targetRatio) {
        const height = vh;
        const width = Math.round(height * targetRatio);
        return { width, height };
    } else {
        const width = vw;
        const height = Math.round(width / targetRatio);
        return { width, height };
    }
};

const calculateDimensions = () => {
    const vw = Math.round(window.innerWidth);
    const vh = Math.round(window.innerHeight);
    return fitContain(vw, vh, LANDSCAPE_RATIO);
};

// Forced-landscape variant of Game.js: the iframe always keeps a 1920x1080 (16:9)
// aspect ratio, scaled to fit the viewport instead of adapting to device orientation.
const GameLandscape = () => {
    const [dimensions, setDimensions] = useState(calculateDimensions());
    const [lobbyReady, setLobbyReady] = useState(false);
    const [activeModal, setActiveModal] = useState(null);
    const [activeConfirmationModal, setActiveConfirmationModal] = useState(null);
    const [activeInfoModal, setActiveInfoModal] = useState(null);
    const [checkoutOverlayUrl, setCheckoutOverlayUrl] = useState(null);
    const [activeCheckoutOverlay, setActiveCheckoutOverlay] = useState(false);
    const [preInitDone, setPreInitDone] = useState(false);
    const [buildValid, setBuildValid] = useState(null); // null=checking, true=ok, false=invalid

    const buildName = getBuildName();

    const deviceIdFromUrl = new URLSearchParams(window.location.search).get("device_id");

    if (deviceIdFromUrl) {
        cookieManager.set(Constants.DEVICE_ID, deviceIdFromUrl);
    }

    // PRE-INIT
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                // Build version
                const buildVersion = getBuildVersion(buildName);
                cookieManager.set(Constants.BUILD_VERSION, buildVersion);

                // Country
                await GeoLocationHelper.resolveCountryByIP("GameLandscape");

                // Tokens
                await setupTokens();

                // uaID
                const uaID = cookieManager.getAfUserId();
                if (!uaID) {
                    TriggerActionService.sendTriggerAction(
                      ActionTriggerConstants.AD_BLOCKER_DETECTED,
                      "",
                      { sourceUrl: "GameLandscape" }
                    ).catch((e) => {
                        console.warn("[-] TriggerActionService failed", e);
                    });
                    ModalManager.openModal("info", "info-id", setActiveInfoModal, "");
                    if (mounted) setPreInitDone(false);
                    return;
                }
                console.log("[GameLandscape] PRE-INIT uaID:", uaID);

                // Set preInit done
                if (mounted) setPreInitDone(uaID !== null && uaID !== "");
            } catch (e) {
                console.warn("[GameLandscape] preInit failed:", e);
                if (mounted) setPreInitDone(false);
            }
        })();
        return () => {
            mounted = false;
        };
    }, []);

    // RESIZE EVENTS
    useEffect(() => {
        TriggerActionService.sendTriggerAction(
            ActionTriggerConstants.LANDING_OPEN,
            "",
            { sourceUrl: "GameLandscape" }
        ).catch((e) => console.warn("[-] TriggerActionService failed", e));

        const handleResize = () => setDimensions(calculateDimensions());

        if (window.visualViewport) {
            window.visualViewport.addEventListener("resize", handleResize);
        }
        window.addEventListener("resize", handleResize);
        handleResize();

        return () => {
            if (window.visualViewport) {
                window.visualViewport.removeEventListener("resize", handleResize);
            }
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    // HANDLE MESSAGES
    useEffect(() => {
        const handleMessage = (event) => {
            // SECURE
            if (!(event.origin.includes("frontend.assets.tangelogames.app") ||
                allowedPaymentOrigin(event.origin) ||
                event.origin === window.origin)) {
                return;
            }

            const { action, modalType, idTask, extraInfo, value } = event.data || {};

            if (action === "onLobbyLoaded") {
                console.log("[GameLandscape] onLobbyLoaded");

                setLobbyReady(true);
            }

            if (action === "openFormModal") {
                let parsedExtra = {};
                try {
                    parsedExtra = typeof extraInfo === "string" && extraInfo ? JSON.parse(extraInfo) : {};
                    console.log("[GameLandscape] openFormModal received:", {action, modalType, parsedExtra});
                } catch (err) {
                    console.warn("[GameLandscape] Failed to parse extraInfo JSON:", err);
                }
                ModalManager.openModal(modalType, idTask, setActiveModal, parsedExtra);
            } else if (action === "setExitDialog") {
                const enableDialog = value === "enable";
                console.log("[GameLandscape] setExitDialog received:", {value});
                if (enableDialog) {
                    addBeforeUnload();
                } else {
                    removeBeforeUnload();
                }
            } else if (action === "openUrl") {
                console.log("[GameLandscape] openUrl received:", {value});
                if (mustRedirectPayments()){
                    redirect(value);
                } else {
                    openCheckoutOverlay(value);
                }
            } else if (action === "closeCheckoutIframe") {
                console.log("[GameLandscape] closeCheckoutIframe received:");
                closeCheckoutOverlay(true);
            } else {
                // Delegate the rest to UnityBridge
                console.log("[GameLandscape] postMessage received", {action, eventData:event.data});
                UnityBridge.handleAction(action, event.data);
            }
        };

        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, []);

    // BUILD VALIDATION
    useEffect(() => {
        if (!buildName) return;
        const url = `https://frontend.assets.tangelogames.app/games/cash/webgl/${buildName}/index.html`;
        fetch(url, { method: 'HEAD' })
            .then(res => {
                if (res.ok) {
                    setBuildValid(true);
                } else {
                    console.error(`[GameLandscape] Build "${buildName}" not found on server (${res.status})`);
                    setBuildValid(false);
                }
            })
            .catch(err => {
                console.error('[GameLandscape] Build validation failed:', err);
                setBuildValid(false);
            });
    }, []);

    let beforeUnloadRegistered = false;

    (function pushHistoryStates(){
      if (!window.history.state || !window.history.state._mundiHistory) {
          window.history.pushState({_mundiHistory: true}, '', window.location.href);
      }
    })();

    const addBeforeUnload = () => {
        if (!beforeUnloadRegistered) {
            window.addEventListener("beforeunload", handleBeforeUnload);
            beforeUnloadRegistered = true;
        }
    };

    const removeBeforeUnload = () => {
        if (beforeUnloadRegistered) {
            window.removeEventListener("beforeunload", handleBeforeUnload);
            beforeUnloadRegistered = false;
        }
    };

    // https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event#examples
    const handleBeforeUnload = (event) => {
        event.preventDefault();
        event.returnValue = true; // legacy support
    };

    // https://stackoverflow.com/a/49719812
    window.addEventListener('popstate', function(event) {
        ModalManager.openModal("exitConfirmation", "confirmation-id", setActiveConfirmationModal, "");
    });

    const openCheckoutOverlay = (url) => {
        setCheckoutOverlayUrl(url);
        setActiveCheckoutOverlay(true);
    }

    const closeCheckoutOverlay = (postToIframe) => {
        setActiveCheckoutOverlay(false);

        if (postToIframe) {
            UnityBridge.handleAction("closeCheckoutIframe", "");
        }

        const gameFrame = document.getElementById("game-iframe");
        gameFrame.focus();
    }

    if (!buildName) {
        console.error("[GameLandscape] No build name resolved — redirecting to 404");
        return <Navigate replace to="/404" />;
    }

    if (buildValid === false) {
        console.error(`[GameLandscape] Build "${buildName}" does not exist — redirecting to 404`);
        return <Navigate replace to="/404" />;
    }

    const indexUrl = `https://frontend.assets.tangelogames.app/games/cash/webgl/${buildName}/index.html`;

    return (
        <div>
        { preInitDone && lobbyReady && <InstallBanner /> }
        <div id="game-container" className="game-container">
            <div id="iframe-wrapper" className="iframe-wrapper">

                {/* Game iframe */}
                {preInitDone && buildValid === true && (
                    <iframe
                        id="game-iframe"
                        className="game-iframe"
                        src={indexUrl}
                        allowFullScreen
                        style={{
                            width: `${dimensions.width}px`,
                            height: `${dimensions.height}px`,
                        }}
                        onLoad={() => {
                            console.log("[GameLandscape.js] Game iframe loaded");
                            TriggerActionService.sendTriggerAction(
                                ActionTriggerConstants.UNITY_LOAD_STARTED,
                                "",
                                { sourceUrl: "GameLandscape" }
                            ).catch((e) => console.warn("[-] TriggerActionService failed", e));
                        }}
                    />
                )}

                {/* Modal */}
                {activeCheckoutOverlay && (
                    <div
                        className="checkout-iframe-wrapper"
                    >
                        <div onClick={(e) => e.stopPropagation()}>
                            <div
                                className="iframe-container"
                                style={{
                                    position: "relative",
                                    width: `${dimensions.width}px`,
                                    height: `${dimensions.height}px`
                                }}
                            >
                                <img
                                    className="checkout-iframe-button"
                                    src="/assets/back.png"
                                    alt={Localize.translateToUpper("LANDING_BUTTON_BACK")}
                                    onClick={() => closeCheckoutOverlay(true)}
                                />

                                <iframe
                                    src={checkoutOverlayUrl}
                                    allow="payment"
                                    title="Mundigames Cash Checkout"
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        border: "none",
                                        borderRadius: "12px",
                                        backgroundColor: "white",
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Show modal */}
                {activeInfoModal && ModalManager.renderModal(activeInfoModal, setActiveInfoModal)}
                {activeModal && ModalManager.renderModal(activeModal, setActiveModal)}
                {/* Show confirmation modal */}
                {activeConfirmationModal && ModalManager.renderModal(activeConfirmationModal, setActiveConfirmationModal)}
            </div>
        </div>
        </div>
    );
};

export default GameLandscape;
